import { Box, IconButton, Stack, Typography } from '@mui/joy';
import { Plus, X } from '@phosphor-icons/react';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { Control, useFieldArray, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Form, FormField } from 'core/components';
import { InputType, NotificationPosition } from 'core/enums';
import { useFormContext } from 'core/states';
import { IRequestPayload } from 'core/types';
import { ICorporatePartCostListItem } from 'corporate/types';
import { uomService } from 'parts/services';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { RemoveCorporatePartCostButton } from './RemoveCorporatePartCostButton';
import { ICorporatePartCostItemForm, IUpsertCorporatePartCostForm } from './types';

interface IProps {
  cost?: ICorporatePartCostListItem;
  isOpen: boolean;
  onClose: (reset?: UseFormReset<IUpsertCorporatePartCostForm>) => void;
  onSubmit: (
    reset?: UseFormReset<IUpsertCorporatePartCostForm>
  ) => (data: IUpsertCorporatePartCostForm) => Promise<void>;
  onItemRemoved?: (itemId: number) => void;
  onRemoveSuccess?: () => void;
}

const newItemTemplate: Partial<ICorporatePartCostItemForm> = {
  cost: undefined,
  minQuantity: undefined,
  maxQuantity: undefined,
};

const dateRowSx = { gap: 2 };
const costingCardSx = {
  backgroundColor: 'background.level1',
  borderRadius: 'sm',
  padding: 1.5,
};
const costingRowSx = { alignItems: 'flex-end', gap: 1 };
const decoratorSx = { color: 'text.tertiary', minWidth: '12px' };
const removeIconSx = { marginBottom: 0.5 };

const getUomBaseOptions = (requestPayload: IRequestPayload, signal: AbortSignal) =>
  uomService.getManyUoms({ ...requestPayload, filters: { isBase: 1 } }, signal);

export const UpsertCorporatePartCostFormFields = ({
  cost,
  isOpen,
  onClose,
  onSubmit,
  onItemRemoved,
  onRemoveSuccess,
}: IProps) => {
  const { t } = useTranslation();

  const { control, watch, errors, getValues } = useFormContext();

  const { fields, append, remove } = useFieldArray<
    IUpsertCorporatePartCostForm,
    'costItems',
    'fieldId'
  >({
    control: control as unknown as Control<IUpsertCorporatePartCostForm>,
    name: 'costItems',
    keyName: 'fieldId',
  });

  const uom = watch('uom') as IUpsertCorporatePartCostForm['uom'];
  const uomLabel = uom?.name;

  const startDate = watch('startDate') as Dayjs | null | undefined;
  const today = dayjs();
  const endDateMinDate = startDate ? dayjs(startDate).add(1, 'day') : today;

  const title = cost ? t('titleEditPartCost') : t('titleAddPartCost');

  const removeButton = useMemo(() => {
    if (!cost) {
      return null;
    }

    return (
      <RemoveCorporatePartCostButton
        costs={[cost]}
        buttonLabel={t('buttonLabelRemovePartCost')}
        onRemoveSuccess={onRemoveSuccess}
        notificationPosition={NotificationPosition.FORM}
      />
    );
  }, [cost, onRemoveSuccess, t]);

  const handleAddCostItem = () => {
    const currentItems = (getValues('costItems') ?? []) as ICorporatePartCostItemForm[];
    const lastItem = currentItems[currentItems.length - 1];
    const lastMax = lastItem?.maxQuantity;
    const nextMinQuantity =
      lastMax != null && (lastMax as unknown) !== '' ? Number(lastMax) : undefined;

    append({
      ...newItemTemplate,
      minQuantity: nextMinQuantity,
    } as ICorporatePartCostItemForm);
  };

  const handleRemoveCostItem = (index: number) => {
    const item = fields[index] as ICorporatePartCostItemForm | undefined;

    if (item?.id) {
      onItemRemoved?.(item.id);
    }

    remove(index);
  };

  return (
    <Form
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      extraButtons={removeButton}
    >
      <Stack direction={Direction.ROW} sx={dateRowSx}>
        <FormField
          name="startDate"
          type={InputType.DATE_INPUT}
          inputProps={{
            label: t('labelStartDate'),
            required: true,
            control,
            error: errors?.startDate,
            minDate: today,
          }}
        />
        <FormField
          name="endDate"
          type={InputType.DATE_INPUT}
          inputProps={{
            label: t('labelEndDate'),
            control,
            error: errors?.endDate,
            minDate: endDateMinDate,
          }}
        />
      </Stack>

      <FormField
        name="uom"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          label: t('labelUom'),
          required: true,
          disableClearable: true,
          getOptions: getUomBaseOptions,
          optionKey: 'name',
          enabledOnFocus: false,
        }}
      />

      <Stack sx={{ gap: 1 }}>
        <Typography level={Level.TITLE_SM}>{t('labelCosting')}</Typography>

        <Box sx={costingCardSx}>
          <Stack sx={{ gap: 1 }}>
            {fields.map((field, index) => {
              const fieldErrors = errors?.costItems?.[index];

              return (
                <Stack key={field.fieldId} direction={Direction.ROW} sx={costingRowSx}>
                  <FormField
                    name={`costItems.${index}.minQuantity`}
                    type={InputType.NUMBER_INPUT}
                    inputProps={{
                      label: t('labelMinQty'),
                      required: true,
                      control,
                      error: fieldErrors?.minQuantity,
                      startDecorator: <Typography sx={decoratorSx}>≥</Typography>,
                    }}
                  />
                  <FormField
                    name={`costItems.${index}.maxQuantity`}
                    type={InputType.NUMBER_INPUT}
                    inputProps={{
                      label: t('labelMaxQty'),
                      control,
                      error: fieldErrors?.maxQuantity,
                      startDecorator: <Typography sx={decoratorSx}>{'<'}</Typography>,
                    }}
                  />
                  <FormField
                    name={`costItems.${index}.cost`}
                    type={InputType.NUMBER_INPUT}
                    inputProps={{
                      label: t('labelCostWithCurrency'),
                      required: true,
                      control,
                      error: fieldErrors?.cost,
                      endDecorator: uomLabel ? (
                        <Typography level={Level.BODY_SM} sx={decoratorSx}>
                          {`/${uomLabel}`}
                        </Typography>
                      ) : null,
                    }}
                  />
                  <IconButton
                    size={Size.SM}
                    color={Color.NEUTRAL}
                    variant={Variant.PLAIN}
                    onClick={() => handleRemoveCostItem(index)}
                    disabled={fields.length === 1}
                    sx={removeIconSx}
                    aria-label={t('buttonLabelRemove')}
                  >
                    <X />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        </Box>

        <Button
          text={t('buttonLabelAddQuantity')}
          color={Color.PRIMARY}
          variant={Variant.SOFT}
          startDecorator={<Plus />}
          onClick={handleAddCostItem}
          sx={{ alignSelf: 'flex-start' }}
        />
      </Stack>
    </Form>
  );
};
